// Частите на черновата в работния лист — общи за доставка, изписване,
// прехвърляне и инвентаризация:
//  • `DraftPresence` — горе вдясно в лентата: „Само вие редактирате" или
//    „{име} пое редакцията — промените ви вече не се пазят";
//  • `DraftFooterActions` — бутоните пред „Запиши" във футъра: нов документ —
//    „Изтрий черновата" (след въпрос с редовете) · „Запази временно";
//    редакция — „Отказ".
import { DeleteOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { StatusPill } from "../list/ListBits";
import {
  DANGER_TEXT_BUTTON_CLASS,
  SECONDARY_BUTTON_CLASS,
} from "../list/buttonStyles";
import { useConfirm } from "../confirm/useConfirm";
import type { ConfirmPreviewItem } from "../confirm/confirmTypes";
import { labelParams, type DraftTexts } from "./draftTexts";

export const DraftPresence = ({ lockLostBy }: { lockLostBy: string | null }) => {
  const { t } = useTranslation();
  return lockLostBy != null ? (
    <StatusPill tone="danger" dot>
      {t("inventoryPage.lockLost", { name: lockLostBy })}
    </StatusPill>
  ) : (
    <StatusPill tone="neutral" dot>
      {t("inventoryPage.presenceOnlyYou")}
    </StatusPill>
  );
};

// Какво показва въпросът „Изтриване на незавършената …?" — четено в момента
export type DraftDeletePreview = {
  label: string | null;
  items: ConfirmPreviewItem[];
  // общият брой, когато `items` е орязан
  total?: number;
};

// Каквото листът подава за черновата си (футърите го носят като един проп)
export type DraftFooterProps = {
  // има какво да се трие: черновата е на сървъра или в листа има редове
  canDeleteDraft: boolean;
  describeDraft: () => DraftDeletePreview;
  // след потвърждение (тече с въртящ се бутон в самия въпрос)
  onDeleteDraft: () => Promise<void>;
  onSaveTemp: () => void;
};

type FooterProps = DraftFooterProps & {
  mode: "new" | "edit";
  texts: DraftTexts;
  onCancel: () => void;
};

export const DraftFooterActions = ({
  mode,
  texts,
  canDeleteDraft,
  describeDraft,
  onDeleteDraft,
  onSaveTemp,
  onCancel,
}: FooterProps) => {
  const { t } = useTranslation();
  const confirm = useConfirm();
  if (mode === "edit") {
    return (
      <button type="button" onClick={onCancel} className={`${SECONDARY_BUTTON_CLASS} !py-[11px]`}>
        {t("inventoryPage.cancel")}
      </button>
    );
  }
  const confirmDelete = () => {
    const draft = describeDraft();
    void confirm({
      intent: "danger",
      icon: <DeleteOutlined />,
      title:
        draft.label && texts.deleteTitleWithLabel
          ? t(texts.deleteTitleWithLabel, labelParams(draft.label))
          : t(texts.deleteTitle),
      explanation: t(texts.deleteText),
      preview: draft.items.length
        ? {
            caption: t("inventoryPage.cfmDeleteDraftCaption"),
            items: draft.items,
            total: draft.total,
          }
        : undefined,
      note: { kind: "final", text: t("inventoryPage.cfmFinal") },
      confirmLabel: t("inventoryPage.deleteDraft"),
      action: onDeleteDraft,
    });
  };
  return (
    <>
      {canDeleteDraft && (
        <button type="button" onClick={confirmDelete} className={DANGER_TEXT_BUTTON_CLASS}>
          {t("inventoryPage.deleteDraft")}
        </button>
      )}
      <button type="button" onClick={onSaveTemp} className={`${SECONDARY_BUTTON_CLASS} !py-[11px]`}>
        {t("inventoryPage.saveTemp")}
      </button>
    </>
  );
};
